import api, { setAdminToken, clearAdminToken } from "./api";

// Back-office "system user" auth (RBAC). Separate from the member OTP flow:
// this is a password login that returns a Sanctum bearer token.
//   POST /api/system/login            { email, password } -> { token, user }
//   GET  /api/system/me                                   -> { user }
//   POST /api/system/forgot-password  { email } -> { message }
//   POST /api/system/change-password  { current_password, password,
//                                        password_confirmation } -> { token, user }
//   POST /api/system/logout

// Logs in and persists the bearer token so subsequent /api/system/* calls are
// authenticated. Returns the user object (id, name, email, roles[] (slugs),
// permissions[], is_active, last_login_at).
export const systemLogin = async (email, password) => {
  const { data } = await api.post("/api/system/login", { email, password });
  if (data?.token) setAdminToken(data.token);
  return data.user;
};

// Changes the logged-in system user's own password (used the first time they
// log in with a temporary password, since login returns must_change_password:
// true). The backend revokes every existing token and issues a brand-new one
// here, so — same as systemLogin — we must persist it or the next request
// would carry the now-revoked login token. Returns the updated user (with
// must_change_password: false).
export const systemChangePassword = async ({ currentPassword, password, passwordConfirmation }) => {
  const { data } = await api.post("/api/system/change-password", {
    current_password: currentPassword,
    password,
    password_confirmation: passwordConfirmation,
  });
  if (data?.token) setAdminToken(data.token);
  return data.user;
};

// Emails a temporary password to a system user who forgot theirs. Rate-limited
// server-side and always resolves with the same generic message regardless of
// whether the email matched an account, so the UI can't be used to enumerate
// which emails belong to real system users. On success the account is forced
// into must_change_password, so the next systemLogin() with that temporary
// password redirects straight to /admin/change-password same as an admin-
// created account's first login.
export const systemForgotPassword = async (email) => {
  const { data } = await api.post("/api/system/forgot-password", { email });
  return data.message;
};

// Re-fetches the current system user from the stored token (used to re-hydrate
// auth state on a page refresh). Throws on 401 if the token is invalid/expired.
export const systemMe = async () => {
  const { data } = await api.get("/api/system/me");
  return data.user;
};

// Logs out server-side (revokes the current token) and clears local storage.
// We clear locally even if the network call fails so the UI always logs out.
export const systemLogout = async () => {
  try {
    await api.post("/api/system/logout");
  } finally {
    clearAdminToken();
  }
};
